import { v4 as uuidv4 } from 'uuid';
import withDraft from '@/utils/draftHelper';
import { states } from '@/constants/lifecycle';
import { responseError } from '@/utils/errors';
import findResult, {
  assignIfExists,
  assignProps,
  assignValue,
  findSelectedInCategory,
  findWithReadStatus,
  hasKey,
  removeItemByCode,
} from '@/utils/resultsHelper';
import actions, { collectionLifeCycle, collectionLists } from '../constants';
import {
  assignEditProps,
  findAllCollections,
  findAllCollectionsWithCode,
  findCollection,
  imageStates,
  mapResult,
  newCollectionProperties,
  openCollection,
  pruneCollectionErrors,
} from './state';
import { collectionPerms } from '../sharing/state/state';

const closeCollection = withDraft((draft, action) => {
  const item = findCollection(draft, action);
  if (item) {
    draft.collections[action.category].results = removeItemByCode(draft.collections[action.category].results, item);
  }
});

const createCollection = withDraft((draft) => {
  draft.activeCollection.saveStatus = states.PENDING;
});

const deleteCollection = withDraft((draft, action) => {
  findAllCollections(draft, action).forEach((category) => {
    action.items.forEach(({ code }) => {
      const item = findResult(draft.collections, category, code);
      if (item) {
        item.destroyStatus = states.PENDING;
        item.destroyErrors = [];
      }
    });
  });
});

const destroyCollection = withDraft((draft, action) => {
  draft.deleteCollectionOpen = false;
  findAllCollections(draft, action).forEach((category) => {
    action.items.forEach(({ code }) => {
      const item = findResult(draft.collections, category, code);
      if (item) {
        draft.collections[category].results = removeItemByCode(draft.collections[category].results, item);
      }
    });
  });
});

const destroyCollectionError = withDraft((draft, action) => {
  draft.deleteCollectionOpen = false;
  findAllCollections(draft, action).forEach((category) => {
    action.items.forEach(({ code, error }) => {
      const item = findResult(draft.collections, category, code);
      if (item) {
        item.destroyStatus = states.ERROR;
        item.destroyErrors = [error];
      }
    });
  });
});

const editCollection = withDraft((draft, action) => {
  const item = findCollection(draft, action);
  assignEditProps(item);
});

const fetchCollection = withDraft((draft, action) => {
  const item = findCollection(draft, action);
  if (item) {
    item.readStatus = states.PENDING;
    item.readErrors = [];
  } else {
    const props = action.isNew ? openCollection(action.code) : {
      code: action.code,
      permissions: collectionPerms(),
      readStatus: states.PENDING,
      readErrors: [],
      copy: {},
    };
    draft.collections[action.category].results.push(props);
  }
});

const hideDeleteCollection = withDraft((draft) => {
  draft.deleteCollectionOpen = false;
  draft.collections.deletion.results = [];
});

const hideSaveCollection = withDraft((draft) => {
  draft.activeCollection = { open: false };
});

const newCollection = withDraft((draft, action) => {
  const item = findCollection(draft, action);
  draft.activeCollection = { open: false };
  item.previousCode = item.code;
  item.copy = {};
  assignIfExists(
    item,
    {
      ...action.result,
      saveStatus: states.SUCCESS,
      readStatus: states.SUCCESS,
      saveErrors: [],
      readErrors: [],
      isDirty: false,
      isEditing: false,
      isNew: false,
      ...imageStates(),
    },
  );
  findWithReadStatus(draft.collections, collectionLists, states.SUCCESS).forEach((category) => {
    draft.collections[category].readStatus = states.PENDING;
    draft.collections[category].readErrors = [];
  });
});

const newCollectionError = withDraft((draft, action) => {
  assignIfExists(draft.activeCollection, {
    saveStatus: states.ERROR,
    saveErrors: [responseError(action)],
  });
});

const resetCollectionImage = withDraft((draft, action) => {
  const item = findCollection(draft, action);
  item.imageAdded = false;
});

const saveCollectionImage = withDraft((draft, action) => {
  const item = findCollection(draft, action);
  item.imageSaveStatus = states.PENDING;
  item.imageSaveErrors = [];
});

const saveCollectionImageError = withDraft((draft, action) => {
  const item = findCollection(draft, action);
  item.imageSaveStatus = states.ERROR;
  item.imageSaveErrors = [responseError(action)];
  item.imageAdded = false;
});

const saveCollectionImageSuccess = withDraft((draft, { code, imageKey }) => {
  findAllCollections(draft, { items: [{ code }] }).forEach((category) => {
    const item = findResult(draft.collections, category, code);
    if (item) {
      item.imageSaveStatus = states.SUCCESS;
      item.imageSaveErrors = [];
      item.imageAdded = false;
      item.imageKey = imageKey;
    }
  });
});

const showCollection = withDraft((draft, action) => {
  const item = findCollection(draft, action);
  if (action.isNew) {
    assignProps(item, { ...action.result });
    draft.newCollectionCode = uuidv4();
  } else {
    assignProps(item, {
      ...action.result,
      readStatus: states.SUCCESS,
      readErrors: [],
      saveStatus: states.NONE,
      saveErrors: [],
      isEditing: false,
      isDirty: false,
    });
  }
});

const showCollectionError = withDraft((draft, action) => {
  const item = findCollection(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const showDeleteCollection = withDraft((draft, action) => {
  const item = findCollection(draft, action);
  draft.deleteCollectionOpen = true;
  draft.collections.deletion.results = [item];
});

const showSaveCollection = withDraft((draft, action) => {
  const item = findCollection(draft, action);
  assignProps(draft.activeCollection, newCollectionProperties(item));
});

const toggleCollectionSelected = withDraft((draft, action) => {
  const item = findCollection(draft, action);
  if (!item.selected && !draft.collections[action.category].multiSelect) {
    const selectedCollections = findSelectedInCategory(draft.collections, action.category);
    selectedCollections.forEach((collection) => assignValue(collection, 'selected', false));
  }
  item.selected = !item.selected;
});

const updateActiveCollectionProperties = withDraft((draft, action) => {
  assignIfExists(draft.activeCollection, action.properties);
  draft.activeCollection.saveErrors = pruneCollectionErrors(draft.activeCollection.saveErrors, action);
});

const updateCollection = withDraft((draft, action) => {
  const item = findCollection(draft, action);
  item.saveStatus = states.PENDING;
  item.saveErrors = [];
});

const updateCollectionCancel = withDraft((draft, action) => {
  const item = findCollection(draft, action);
  item.copy = {};
  assignProps(item, {
    saveStatus: states.NONE,
    saveErrors: [],
    isDirty: false,
    isEditing: false,
  });
});

const updateCollectionError = withDraft((draft, action) => {
  const item = findCollection(draft, action);
  assignIfExists(item, {
    saveStatus: states.ERROR,
    saveErrors: [responseError(action)],
  });
});

const updateCollectionProperties = withDraft((draft, action) => {
  const item = findCollection(draft, action);
  assignProps(item, action.properties, collectionLifeCycle, true);
  item.saveErrors = pruneCollectionErrors(item.saveErrors, action);
});

const updateCollectionSuccess = withDraft((draft, action) => {
  findAllCollectionsWithCode(draft, action).forEach((category) => {
    const item = findResult(draft.collections, category, action.code);
    const result = mapResult(action.result);
    if (hasKey(item, 'copy')) {
      item.copy = {};
    }
    assignIfExists(
      item,
      {
        ...result,
        saveStatus: states.SUCCESS,
        saveErrors: [],
        isDirty: false,
        isEditing: false,
      },
    );
  });
});

export default {
  [actions.CLOSE_COLLECTION]: closeCollection,
  [actions.CREATE_COLLECTION]: createCollection,
  [actions.DELETE_COLLECTION]: deleteCollection,
  [actions.DESTROY_COLLECTION]: destroyCollection,
  [actions.DESTROY_COLLECTION_ERROR]: destroyCollectionError,
  [actions.EDIT_COLLECTION]: editCollection,
  [actions.FETCH_COLLECTION]: fetchCollection,
  [actions.HIDE_DELETE_COLLECTION]: hideDeleteCollection,
  [actions.HIDE_SAVE_COLLECTION]: hideSaveCollection,
  [actions.NEW_COLLECTION]: newCollection,
  [actions.NEW_COLLECTION_ERROR]: newCollectionError,
  [actions.RESET_COLLECTION_IMAGE]: resetCollectionImage,
  [actions.SAVE_COLLECTION_IMAGE]: saveCollectionImage,
  [actions.SAVE_COLLECTION_IMAGE_ERROR]: saveCollectionImageError,
  [actions.SAVE_COLLECTION_IMAGE_SUCCESS]: saveCollectionImageSuccess,
  [actions.SHOW_COLLECTION]: showCollection,
  [actions.SHOW_COLLECTION_ERROR]: showCollectionError,
  [actions.SHOW_DELETE_COLLECTION]: showDeleteCollection,
  [actions.SHOW_SAVE_COLLECTION]: showSaveCollection,
  [actions.TOGGLE_COLLECTION_SELECTED]: toggleCollectionSelected,
  [actions.UPDATE_ACTIVE_COLLECTION_PROPERTIES]: updateActiveCollectionProperties,
  [actions.UPDATE_COLLECTION]: updateCollection,
  [actions.UPDATE_COLLECTION_CANCEL]: updateCollectionCancel,
  [actions.UPDATE_COLLECTION_ERROR]: updateCollectionError,
  [actions.UPDATE_COLLECTION_PROPERTIES]: updateCollectionProperties,
  [actions.UPDATE_COLLECTION_SUCCESS]: updateCollectionSuccess,
};
